import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Star, TrendingUp, Users, Clock, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSimpleAuth } from '@/contexts/SimpleAuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

// ── Mock data for complete UI/UX preview ──

const mockReceivedReviews = [
  { id: 'r1', user_name: 'John Anderson', rating: 5, comment: 'Excellent session! Really helped me understand my anxiety issues and gave practical tools to manage stress.', date: '2024-01-14', verified: true },
  { id: 'r2', user_name: 'Lisa Martinez', rating: 5, comment: 'Great experience. The expert was very professional and caring. I felt heard and understood throughout.', date: '2024-01-12', verified: true },
  { id: 'r3', user_name: 'David Kim', rating: 4, comment: 'Good session overall. Would recommend to others looking for career guidance.', date: '2024-01-10', verified: false },
  { id: 'r4', user_name: 'Priya Sharma', rating: 5, comment: 'Absolutely wonderful! The strategies shared were incredibly practical and I started seeing results within a week.', date: '2024-01-05', verified: true },
  { id: 'r5', user_name: 'Emma Thompson', rating: 3, comment: 'Decent session. I expected a bit more depth in the discussion.', date: '2023-12-28', verified: false },
];

const mockPendingSessions = [
  { id: 'ps1', user_id: 'u1', user_name: 'Sarah Wilson', appointment_date: '2024-01-18', session_type: 'Therapy Session', sessions_count: 5 },
  { id: 'ps2', user_id: 'u2', user_name: 'Alex Rivera', appointment_date: '2024-01-16', session_type: 'Career Counseling', sessions_count: 2 },
  { id: 'ps3', user_id: 'u3', user_name: 'Neha Gupta', appointment_date: '2024-01-14', session_type: 'Life Coaching', sessions_count: 1 },
];

const mockReviewedClients = [
  { id: 'rc1', user_name: 'Michael Chen', rating: 5, comment: 'Very engaged and committed to the process. A pleasure to work with. Shows great progress between sessions.', date: '2024-01-12' },
  { id: 'rc2', user_name: 'Jessica Brown', rating: 4, comment: 'Dedicated client who follows through on action items. Very punctual and respectful.', date: '2024-01-08' },
  { id: 'rc3', user_name: 'Raj Patel', rating: 5, comment: 'Outstanding client. Very open to feedback and consistently implements suggestions.', date: '2023-12-20' },
];

const ExpertReviewsPage: React.FC = () => {
  const { expert } = useSimpleAuth();
  const [reviews, setReviews] = useState<any[]>(mockReceivedReviews);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    averageRating: 4.4,
    totalReviews: 5,
    ratingDistribution: { 5: 3, 4: 1, 3: 1, 2: 0, 1: 0 } as Record<number, number>
  });

  // Rate Clients state
  const [pendingSessions, setPendingSessions] = useState<any[]>(mockPendingSessions);
  const [reviewedClients, setReviewedClients] = useState<any[]>(mockReviewedClients);
  const [ratingFormData, setRatingFormData] = useState<{ [key: string]: { rating: number; comment: string } }>({});

  const calculateStats = (reviewsData: any[]) => {
    const totalReviews = reviewsData.length;
    const averageRating = totalReviews > 0 
      ? reviewsData.reduce((sum, review) => sum + review.rating, 0) / totalReviews 
      : 0;
    
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviewsData.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++;
    });

    setStats({ averageRating, totalReviews, ratingDistribution: distribution });
  };

  const renderStars = (rating: number, interactive?: boolean, sessionId?: string) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'
        } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
        onClick={() => {
          if (interactive && sessionId) {
            setRatingFormData(prev => ({
              ...prev,
              [sessionId]: { ...prev[sessionId], rating: i + 1, comment: prev[sessionId]?.comment || '' }
            }));
          }
        }}
      />
    ));
  };

  const handleSubmitReview = (sessionId: string) => {
    const data = ratingFormData[sessionId];
    if (!data?.rating) {
      toast.error('Please select a rating');
      return;
    }
    // When backend is ready, save to expert_reviews table
    toast.success('Review submitted successfully');
    setPendingSessions(prev => prev.filter(s => s.id !== sessionId));
    setRatingFormData(prev => {
      const copy = { ...prev };
      delete copy[sessionId];
      return copy;
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="p-6">Loading reviews...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Reviews & Ratings</h1>
      </div>

      <Tabs defaultValue="received" className="w-full">
        <TabsList className="w-full max-w-md">
          <TabsTrigger value="received" className="flex-1">Reviews Received</TabsTrigger>
          <TabsTrigger value="rate-clients" className="flex-1">Rate Clients</TabsTrigger>
        </TabsList>

        {/* Reviews Received Tab */}
        <TabsContent value="received" className="space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Average Rating</p>
                    <p className="text-2xl font-bold">{stats.averageRating.toFixed(1)}</p>
                    <div className="flex mt-1">{renderStars(Math.round(stats.averageRating))}</div>
                  </div>
                  <Star className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Reviews</p>
                    <p className="text-2xl font-bold">{stats.totalReviews}</p>
                  </div>
                  <Users className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">5-Star Reviews</p>
                    <p className="text-2xl font-bold">{stats.ratingDistribution[5]}</p>
                    <p className="text-sm text-muted-foreground">
                      {stats.totalReviews > 0 ? Math.round((stats.ratingDistribution[5] / stats.totalReviews) * 100) : 0}%
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <Star className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No reviews yet</p>
                <p className="text-sm text-muted-foreground">Complete sessions to start receiving reviews</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar>
                            <AvatarFallback>
                              {review.user_name?.slice(0, 2).toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold">{review.user_name || 'Anonymous'}</h3>
                            <div className="flex items-center gap-2">
                              <div className="flex">{renderStars(review.rating)}</div>
                              <span className="text-sm text-muted-foreground">{formatDate(review.date)}</span>
                            </div>
                          </div>
                          {review.verified && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 ml-auto">
                              Verified
                            </Badge>
                          )}
                        </div>
                        {review.comment && (
                          <p className="text-muted-foreground">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Rate Clients Tab */}
        <TabsContent value="rate-clients" className="space-y-6">
          {/* Pending Reviews */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-yellow-500" />
                Pending Reviews ({pendingSessions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingSessions.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                  <p className="text-muted-foreground">All caught up! No pending reviews.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingSessions.map((session) => {
                    const formData = ratingFormData[session.id] || { rating: 0, comment: '' };
                    return (
                      <div key={session.id} className="p-4 border rounded-lg bg-yellow-50/50 border-yellow-200/50 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {session.user_id?.slice(0, 2).toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">User</p>
                              <p className="text-sm text-muted-foreground">
                                Session on {formatDate(session.appointment_date)}
                              </p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                            Pending
                          </Badge>
                        </div>
                        
                        <div>
                          <p className="text-sm font-medium mb-1">Your Rating</p>
                          <div className="flex gap-1">
                            {renderStars(formData.rating, true, session.id)}
                          </div>
                        </div>

                        <Textarea
                          placeholder="Write your review about this client..."
                          value={formData.comment}
                          onChange={(e) => setRatingFormData(prev => ({
                            ...prev,
                            [session.id]: { ...prev[session.id], rating: prev[session.id]?.rating || 0, comment: e.target.value }
                          }))}
                          className="min-h-[80px]"
                        />

                        <Button 
                          onClick={() => handleSubmitReview(session.id)}
                          className="w-full"
                          disabled={!formData.rating}
                        >
                          <Star className="h-4 w-4 mr-2" />
                          Submit Review
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Already Reviewed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Already Reviewed ({reviewedClients.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reviewedClients.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">No reviews given yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviewedClients.map((review: any) => (
                    <div key={review.id} className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className="bg-green-100 text-green-700">
                              {review.user_name?.slice(0, 2).toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{review.user_name || 'User'}</p>
                            <div className="flex items-center gap-2">
                              <div className="flex">{renderStars(review.rating)}</div>
                              <span className="text-sm text-muted-foreground">{formatDate(review.date)}</span>
                            </div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Reviewed
                        </Badge>
                      </div>
                      {review.comment && (
                        <p className="text-muted-foreground text-sm">{review.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpertReviewsPage;
